import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
  anyFnExec,
  CellContext,
  ColorComponent,
  ColorOptions,
  GridData,
  SelectCellConfig,
  SelectionItem,
} from '../../../models';
import { FocusManagerService } from '../../../services';
import { GridCellContentAlignment } from '../../../enums';
import { FormControl } from '@angular/forms';
import { ReplaySubject, tap } from 'rxjs';
import { fullTrim } from '../../../utils/string';

@UntilDestroy()
@Component({
  selector: 'app-select-edit',
  templateUrl: './select-edit.component.html',
  styleUrls: ['./select-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectEditComponent
  extends ColorComponent<GridData>
  implements AfterViewInit, OnInit
{
  @Input({ required: true }) context!: CellContext<GridData>;

  @ViewChild(MatSelect, { static: true }) select!: MatSelect;

  options!: SelectionItem[];
  filteredOptions$ = new ReplaySubject<SelectionItem[]>(1);
  filterControl = new FormControl<string | null>(null);

  GridCellContentAlignment = GridCellContentAlignment;

  constructor(protected focusManager: FocusManagerService) {
    super();
  }

  get config(): SelectCellConfig<GridData> {
    return this.context.config as SelectCellConfig<GridData>;
  }

  protected get contextColorOptions(): ColorOptions {
    return this.config.colorOptions;
  }

  ngOnInit(): void {
    this.options = anyFnExec(this.config?.options, this.context.data);
    this.init();
  }

  ngAfterViewInit(): void {
    this.context.cell.actions?.setDelegateEditState(true);

    setTimeout(() => {
      this.select.openedChange
        .pipe(untilDestroyed(this))
        .subscribe((opened) => {
          // oce the panel is closed we will let the grid decide when to exit from edit mode
          this.context.cell.actions?.setDelegateEditState(opened);

          // CORNER CASE: popup is closed by clicking outside of the popup, focus is lost and set to document,
          // we need to restore the focus to the cell
          if (!opened && this.focusManager.focused.value === null) {
            return this.select.focus();
          }

          // CORNER CASE: focus is set to a different component by keyboard navigation
          // we need to exit edit state
          if (
            !opened &&
            !this.focusManager.isFocused(this.context.cell.element)
          ) {
            return this.context.cell.actions?.endEdit();
          }
        });

      if (this.config?.autoOpen !== false) {
        this.select.open();
      }
    });
  }

  trackByFunction(_index: number, item: SelectionItem): string | number {
    return item.id;
  }

  //#region Private methods

  init(): void {
    // add the initial list values
    this.filteredOptions$.next([...this.options]);

    // listen for search field value changes
    this.filterControl.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.filterItems();
        })
      )
      .subscribe();
  }

  private filterItems(): void {
    if (!this.options) {
      return;
    }
    // get the search keyword
    const search = fullTrim(this.filterControl.value)?.toLowerCase();

    if (!search) {
      return this.filteredOptions$.next([...this.options]);
    }

    // filter the values
    this.filteredOptions$.next(
      this.options.filter((x) => x.name.toLowerCase().indexOf(search) > -1)
    );
  }

  //#endregion
}
